import React, { useState, useEffect } from 'react';
import { Eye, MessageSquare, Star, Heart, CheckCircle2, Circle, X, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const ContentAssets = () => {
  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalBrand, setGlobalBrand] = useState(localStorage.getItem('globalBrand') || 'all');
  
  const [selectedIds, setSelectedIds] = useState([]);
  const [detailModal, setDetailModal] = useState(null);
  
  const [keywords, setKeywords] = useState([]);
  const [selectedKeyword, setSelectedKeyword] = useState('全部');
  const [activeTab, setActiveTab] = useState('organic');
  const [isKeywordsExpanded, setIsKeywordsExpanded] = useState(false);
  const MAX_VISIBLE_KEYWORDS = 10;

  useEffect(() => {
    const handleGlobalFilterChange = () => {
      setGlobalBrand(localStorage.getItem('globalBrand') || 'all');
    };
    window.addEventListener('globalFilterChange', handleGlobalFilterChange);
    return () => window.removeEventListener('globalFilterChange', handleGlobalFilterChange);
  }, []);

  useEffect(() => {
    if (globalBrand && globalBrand !== 'all') {
      fetchAssets();
    } else {
      setLoading(false);
      setNotes([]);
      setFilteredNotes([]);
      setKeywords([]);
    }
  }, [globalBrand]);

  useEffect(() => {
    // 1. Filter by keyword
    let current = notes;
    if (selectedKeyword !== '全部') {
      current = current.filter(n => n.keyword === selectedKeyword);
    }

    // 2. Filter by tab (organic vs aio)
    current = current.filter(n => {
      if (activeTab === 'organic') return n.sourceScope === 'organic' || !n.sourceScope; // fallback for older ones without sourceScope
      return n.sourceScope && n.sourceScope !== 'organic'; // AIO notes
    });

    // 3. Deduplicate (especially important for '全部')
    const uniqueMap = new Map();
    current.forEach(n => {
      const key = n.noteId || n.noteUrl;
      if (key) {
        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, { ...n });
        } else {
          // merge keywords if we are in '全部' view
          if (selectedKeyword === '全部') {
            const existing = uniqueMap.get(key);
            if (!existing.keyword.includes(n.keyword)) {
              existing.keyword += ', ' + n.keyword;
            }
          }
        }
      }
    });

    setFilteredNotes(Array.from(uniqueMap.values()));
    setSelectedIds([]);
  }, [selectedKeyword, activeTab, notes]);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/content-assets/xhs-notes`, {
        params: { brandId: globalBrand, page: 1, pageSize: 500 }
      });
      if (res.data && res.data.data) {
        const mappedNotes = res.data.data.map(n => ({
          ...n,
          keyword: n.geoResult?.question || '未知关键词'
        }));
        setNotes(mappedNotes);
        const uniqueKeywords = [...new Set(mappedNotes.map(n => n.keyword))];
        setKeywords(uniqueKeywords);
        setSelectedKeyword('全部');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredNotes.length && filteredNotes.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredNotes.map(n => n.id));
    }
  };

  const getAbsoluteUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `https://www.sapientrank.com${url}`;
  };

  const handleBatchDeepLink = async () => {
    if (selectedIds.length === 0) return;
    
    const selectedNotes = notes.filter(n => selectedIds.includes(n.id));
    const assets = selectedNotes.map(n => ({
      name: `[思序] ${n.title || n.keyword || '无标题'}`,
      scenarioType: 'xhs_seeding',
      configMeta: {
        title: n.title,
        content: n.content ? n.content.substring(0, 2000) : '',
        sourceUrl: n.noteUrl || n.noteId,
        coverUrl: getAbsoluteUrl(n.coverUrl)
      }
    }));

    // 先同步打开新窗口防止被拦截
    const newWindow = window.open('about:blank', '_blank');
    if (!newWindow) {
      alert('您的浏览器拦截了弹出窗口，请允许弹出后重试。');
      return;
    }

    try {
      const res = await api.post('/zhimo/handover-to-ui', { assets });
      if (res.data && res.data.redirectUrl) {
        newWindow.location.href = res.data.redirectUrl;
      } else {
        newWindow.close();
        alert('跳转智墨失败：服务器未返回有效的流转链接');
      }
    } catch(err) {
      newWindow.close();
      console.error(err);
      alert('跳转智墨失败：' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2996cf]"></div></div>;
  }

  if (!globalBrand || globalBrand === 'all') {
    return <div className="text-center text-gray-500 dark:text-gray-400 mt-20">请先在顶部导航栏选择一个具体的【品牌项目】</div>;
  }

  return (
    <div className="p-6 min-h-screen">
      
      {/* Top Keyword Filter Bar */}
      {keywords.length > 0 && (
        <div className="mb-6">
           <div className="flex flex-wrap gap-2 items-center">
             <button 
               className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${selectedKeyword === '全部' ? 'bg-[#2996cf] text-white shadow-md' : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
               onClick={() => setSelectedKeyword('全部')}
             >
               全部
             </button>
             {(isKeywordsExpanded ? keywords : keywords.slice(0, MAX_VISIBLE_KEYWORDS)).map(kw => (
               <button 
                 key={kw}
                 className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${selectedKeyword === kw ? 'bg-[#2996cf] text-white shadow-md' : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
                 onClick={() => setSelectedKeyword(kw)}
               >
                 {kw}
               </button>
             ))}
             {keywords.length > MAX_VISIBLE_KEYWORDS && (
               <button 
                 className="px-4 py-1.5 rounded-full text-sm font-medium transition bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-600"
                 onClick={() => setIsKeywordsExpanded(!isKeywordsExpanded)}
               >
                 {isKeywordsExpanded ? '收起 ↑' : `展开更多 (${keywords.length - MAX_VISIBLE_KEYWORDS}) \u2193`}
               </button>
             )}
           </div>
        </div>
      )}

      {/* Tabs for Source Scope */}
      <div className="mb-6 border-b border-gray-200 dark:border-slate-700 flex gap-6">
        <button
          className={`pb-3 text-[15px] font-medium transition relative ${activeTab === 'organic' ? 'text-[#2996cf]' : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300'}`}
          onClick={() => setActiveTab('organic')}
        >
          小红书自然排名笔记
          {activeTab === 'organic' && <div className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-[#2996cf] rounded-t-full"></div>}
        </button>
        <button
          className={`pb-3 text-[15px] font-medium transition relative ${activeTab === 'aio' ? 'text-[#ff2442]' : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300'}`}
          onClick={() => setActiveTab('aio')}
        >
          AIO 引用溯源 (小红书笔记)
          {activeTab === 'aio' && <div className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-[#ff2442] rounded-t-full"></div>}
        </button>
      </div>

      {/* Top Actions Bar */}
      <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 flex flex-col md:flex-row justify-between items-center mb-6">
        <div className="flex items-center gap-4 mb-4 md:mb-0">
           <div className="flex items-center gap-2 cursor-pointer" onClick={toggleSelectAll}>
             {selectedIds.length === filteredNotes.length && filteredNotes.length > 0 ? 
               <CheckCircle2 className="w-5 h-5 text-[#2996cf]" /> : 
               <Circle className="w-5 h-5 text-gray-400 dark:text-slate-500" />
             }
             <span className="text-gray-700 dark:text-slate-300 font-medium">全选本页</span>
           </div>
           <div className="bg-blue-50 dark:bg-[#2996cf]/20 text-[#2996cf] dark:text-[#5cbdf0] px-3 py-1.5 rounded-md text-sm font-medium">
             已选择 {selectedIds.length} 项
           </div>
        </div>
        <div className="flex gap-3">
           <button className="px-5 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition font-medium">
             重新抓取
           </button>
           <button 
             className={`px-5 py-2 rounded-lg text-white font-medium flex items-center gap-2 transition ${selectedIds.length > 0 ? 'bg-[#3b82f6] hover:bg-blue-600 shadow-sm cursor-pointer' : 'bg-blue-300 dark:bg-slate-600 cursor-not-allowed opacity-80'}`}
             onClick={handleBatchDeepLink}
           >
             去智墨生成 (Sapient Ink) →
           </button>
        </div>
      </div>

      {filteredNotes.length === 0 ? (
        <div className="text-center text-gray-500 dark:text-gray-400 mt-20">当前暂无溯源笔记数据</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredNotes.map(note => (
            <div key={note.id} className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden shadow-sm hover:shadow-md border border-gray-100 dark:border-slate-700 transition group cursor-pointer" onClick={() => setDetailModal(note)}>
               <div className="relative aspect-video bg-gray-100 dark:bg-slate-900 overflow-hidden">
                  <img src={note.coverUrl || note.image || note.cover || note.pic} className="w-full h-full object-cover" referrerPolicy="no-referrer" onError={(e) => { if (!e.target.dataset.failed) { e.target.dataset.failed = true; e.target.src = "/assets/fallback-image.png"; } }} />
                  
                  {/* Select Icon */}
                  <div className="absolute top-3 left-3 z-10" onClick={(e) => toggleSelect(note.id, e)}>
                     {selectedIds.includes(note.id) ? 
                       <div className="bg-white dark:bg-slate-800 rounded-full shadow-md"><CheckCircle2 className="w-7 h-7 text-[#2996cf]" /></div> : 
                       <div className="bg-black/20 rounded-full"><Circle className="w-7 h-7 text-white" /></div>
                     }
                  </div>

                  {/* Type Tag */}
                  <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded flex items-center gap-1 z-10">
                     <MessageSquare className="w-3 h-3" /> 图文
                  </div>

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300">
                     <div className="bg-white dark:bg-slate-800 text-gray-900 dark:text-white px-6 py-2.5 rounded-full font-medium shadow-lg transform -translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                       查看详情
                     </div>
                  </div>
               </div>
               
               <div className="p-4">
                  {/* Author & Date */}
                  <div className="flex justify-between items-center mb-3">
                     <div className="flex items-center gap-2">
                       <img src={note.avatarUrl || "/assets/logo.png"} className="w-6 h-6 rounded-full border border-gray-100 dark:border-slate-700" referrerPolicy="no-referrer" onError={(e) => { if (!e.target.dataset.failed) { e.target.dataset.failed = true; e.target.src = "/assets/logo.png"; } }} />
                       <span className="text-gray-700 dark:text-slate-300 text-sm font-medium truncate max-w-[120px]">{note.nickname}</span>
                     </div>
                     <span className="text-gray-400 dark:text-slate-500 text-xs">{note.timeText?.substring(0,10)}</span>
                  </div>
                  
                  {/* Title */}
                  <h3 className="font-bold text-gray-900 dark:text-slate-100 text-[15px] mb-2 truncate" title={note.title}>{note.title}</h3>
                  
                  {/* Snippet */}
                  <p className="text-gray-500 dark:text-slate-400 text-sm line-clamp-2 mb-4 h-10 leading-5">
                     {note.content}
                  </p>

                  {/* Footer */}
                  <div className="flex justify-between items-center text-gray-400 dark:text-slate-500 text-sm pt-2">
                     <div className="flex gap-4">
                        <span className="flex items-center gap-1.5"><Heart className="w-4 h-4" /> {note.likeCount}</span>
                        <span className="flex items-center gap-1.5"><Star className="w-4 h-4" /> {note.collectCount}</span>
                        <span className="flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> {note.commentCount || 0}</span>
                     </div>
                     <div 
                        className="text-[#2996cf] hover:text-[#1a7eb3] flex items-center gap-1 z-10" 
                        onClick={(e) => { e.stopPropagation(); window.open(note.noteUrl, '_blank'); }}
                     >
                       原文 →
                     </div>
                  </div>
               </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {detailModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 md:p-8" onClick={() => setDetailModal(null)}>
           
           {/* Prev Button */}
           {filteredNotes.findIndex(n => n.id === detailModal.id) > 0 && (
             <button 
               className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition backdrop-blur-md hidden md:block"
               onClick={(e) => {
                 e.stopPropagation();
                 const idx = filteredNotes.findIndex(n => n.id === detailModal.id);
                 if (idx > 0) setDetailModal(filteredNotes[idx - 1]);
               }}
             >
               <ChevronLeft className="w-8 h-8" />
             </button>
           )}

           {/* Next Button */}
           {filteredNotes.findIndex(n => n.id === detailModal.id) < filteredNotes.length - 1 && (
             <button 
               className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition backdrop-blur-md hidden md:block"
               onClick={(e) => {
                 e.stopPropagation();
                 const idx = filteredNotes.findIndex(n => n.id === detailModal.id);
                 if (idx < filteredNotes.length - 1) setDetailModal(filteredNotes[idx + 1]);
               }}
             >
               <ChevronRight className="w-8 h-8" />
             </button>
           )}

           <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-5xl h-[85vh] overflow-hidden flex flex-col md:flex-row shadow-2xl relative" onClick={e => e.stopPropagation()}>
              
              {/* Left: Image */}
              <div className="md:w-[55%] bg-black h-64 md:h-full relative flex items-center justify-center">
                 <img src={detailModal.coverUrl || detailModal.image || detailModal.cover || detailModal.pic} className="max-w-full max-h-full object-contain" referrerPolicy="no-referrer" onError={(e) => { if (!e.target.dataset.failed) { e.target.dataset.failed = true; e.target.src = "/assets/fallback-image.png"; } }} />
              </div>
              
              {/* Right: Content */}
              <div className="md:w-[45%] flex flex-col h-full bg-white dark:bg-slate-800">
                 <div className="p-4 border-b border-gray-100 dark:border-slate-700 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                       <img src={detailModal.avatarUrl || "/assets/logo.png"} className="w-10 h-10 rounded-full border border-gray-200 dark:border-slate-700" referrerPolicy="no-referrer" onError={(e) => { if (!e.target.dataset.failed) { e.target.dataset.failed = true; e.target.src = "/assets/logo.png"; } }} />
                       <div>
                          <div className="font-bold text-gray-900 dark:text-slate-100 text-sm">{detailModal.nickname}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{detailModal.timeText}</div>
                       </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition" onClick={() => setDetailModal(null)}>
                       <X className="w-5 h-5" />
                    </button>
                 </div>
                 
                 <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 mb-4 leading-snug">{detailModal.title}</h2>
                    <p className="text-gray-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed text-[15px]">{detailModal.content}</p>
                    {/* Source Keyword tag */}
                    <div className="mt-6 flex flex-wrap gap-2">
                       <span className="bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 px-3 py-1 rounded-md text-xs font-medium border border-gray-200 dark:border-slate-600">
                         搜索关键词：{detailModal.keyword}
                       </span>
                    </div>
                 </div>
                 
                 <div className="p-5 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/80 flex flex-col gap-4">
                    <div className="flex gap-6 text-gray-600 dark:text-slate-400 pl-1">
                       <span className="flex items-center gap-2 font-medium"><Heart className="w-5 h-5" /> {detailModal.likeCount}</span>
                       <span className="flex items-center gap-2 font-medium"><Star className="w-5 h-5" /> {detailModal.collectCount}</span>
                       <span className="flex items-center gap-2 font-medium"><MessageSquare className="w-5 h-5" /> {detailModal.commentCount || 0}</span>
                    </div>
                    <button 
                       className={`w-full py-3 rounded-xl font-medium shadow-sm transition flex items-center justify-center gap-2 ${selectedIds.includes(detailModal.id) ? 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600' : 'bg-[#2996cf] hover:bg-[#1a7eb3] text-white'}`}
                       onClick={() => toggleSelect(detailModal.id, { stopPropagation: () => {} })}
                    >
                       {selectedIds.includes(detailModal.id) ? (
                         <>
                           <CheckCircle2 className="w-5 h-5 text-[#2996cf] dark:text-[#5cbdf0]" /> 移出待仿写清单
                         </>
                       ) : (
                         <>
                           <Zap className="w-5 h-5" /> 加入待仿写清单
                         </>
                       )}
                    </button>
                 </div>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};
export default ContentAssets;
